import React, { useEffect, useRef, useState } from "react";
import { navigate } from "gatsby";
import { ButtonColour } from "@trussle/external-components";
import throttle from "lodash.throttle";
import { StampDutyCalculatorResults } from "./StampDutyCalculatorResults";
import { getChargesAndDescriptionsToShow } from "./calculationsAndDescriptions";
import { StampDutyForm } from "./forms/StampDutyForm";
import { IContentfulRichText } from "../../../../types/IContentfulRichText";
import { IStampDutyCountry, IStampDutyYesNoFirstTimeBuyer, IStampDutyYesNoOnlyProperty } from "./forms/types";
import { convertCurrencyStringToFloat } from "../calculatorMethods";
import { CalculatorTemplate } from "../../../../templates/CalculatorTemplate";
import { trackEvent } from "../../../../helpers/analytics";

const DEBOUNCE_RESULTS = 1000;

export interface IStampDutyCalculatorPanelProps {
  id: string;
  title: string;
  description: IContentfulRichText;
  disclaimer: IContentfulRichText;
  ctaText: string;
  ctaUrl: string;
  secondCtaText: string;
  secondCtaUrl: string;
  fastestLender: IContentfulRichText;
}

export const currencyFormatter = new Intl.NumberFormat("en-GB", {
  currency: "GBP",
  style: "currency",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const updateResult = async (
  tryToIncreaseNumberOfUsage: () => Promise<boolean>,
  setResult: (amount: number) => void,
  result: number,
) => {
  if (!await tryToIncreaseNumberOfUsage()) {
    setResult(result);
  }
};

const throttleResult = throttle(updateResult, DEBOUNCE_RESULTS, {
  leading: false,
  trailing: true,
});

export const StampDutyCalculatorPanel: React.FC<IStampDutyCalculatorPanelProps> = ({
  title,
  description,
  disclaimer,
  ctaText,
  ctaUrl,
  secondCtaText,
  secondCtaUrl,
  fastestLender,
}) => {
  const [propertyValue, setPropertyValue] = useState("");
  const [country, setCountry] = useState<IStampDutyCountry>(IStampDutyCountry.ENG_NI);
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState<IStampDutyYesNoFirstTimeBuyer>();
  const [isOnlyPropertyRaw, setIsOnlyProperty] = useState<IStampDutyYesNoOnlyProperty>();
  const shouldDisabledIsOnlyProperty = isFirstTimeBuyer === IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER;
  const isOnlyProperty = shouldDisabledIsOnlyProperty ? IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY : isOnlyPropertyRaw;

  const chargesAndDescriptionsToShow = getChargesAndDescriptionsToShow({
    propertyValue: propertyValue !== "" ? convertCurrencyStringToFloat(propertyValue) : undefined,
    country,
    isFirstTimeBuyer: isFirstTimeBuyer !== undefined ? isFirstTimeBuyer === IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER : undefined,
    isOnlyProperty: isOnlyProperty !== undefined ? isOnlyProperty === IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY : undefined,
  });

  const stampDutyRawResult = chargesAndDescriptionsToShow;
  const [stampDutyResult, setStampDutyResult] = useState(0);

  const firstRenderStampDuty = useRef(false);

  useEffect(() => {
    throttleResult(
      async () => {
        if (firstRenderStampDuty.current === false) {
          firstRenderStampDuty.current = true;
          return false;
        }
        return false;
      },
      setStampDutyResult,
      stampDutyRawResult,
    );
  }, [stampDutyRawResult]);

  return (
    <>
      <CalculatorTemplate
        title={title}
        description={description}
        disclaimer={disclaimer}
        defaultTab={0}
        isAtTopOfPage={false}
        id="stamp-duty-calculator"
        tabInformation={
        [
          {
            form: (
              <StampDutyForm
                propertyValue={propertyValue}
                setPropertyValue={setPropertyValue}
                country={country}
                setCountry={setCountry}
                isFirstTimeBuyer={isFirstTimeBuyer}
                setIsFirstTimeBuyer={setIsFirstTimeBuyer}
                isOnlyProperty={isOnlyProperty}
                setIsOnlyProperty={setIsOnlyProperty}
                shouldDisabledIsOnlyProperty={shouldDisabledIsOnlyProperty}
              />
            ),
            calculatorResults: {
              bottom: {},
              cta: {
                title: ctaText,
                url: ctaUrl,
                handleClick: (e) => {
                  e.preventDefault();
                  trackEvent("First CTA Clicked", {
                    module: "Stamp Duty Calculator",
                  });
                  navigate(ctaUrl);
                },
              },
              secondCta: {
                title: secondCtaText,
                url: secondCtaUrl,
                handleClick: (e) => {
                  e.preventDefault();
                  trackEvent("Second CTA Clicked", {
                    module: "Stamp Duty Calculator",
                  });
                  navigate(secondCtaUrl);
                },
              },
              secondCtaColor: ButtonColour.Secondary,
              footer: {
                heading: "Next steps",
                text: fastestLender,
              },
              middle: {},
              secondMiddle: {},
              top: {
                result: stampDutyResult,
              },
              TopResultComponent: () => <StampDutyCalculatorResults stampDutyResult={stampDutyResult} />,
            },
            title,
          },
        ]
      }
      />
    </>
  );
};
